import { useSyncExternalStore } from 'react';
import type { AreaDto, ChatMessageDto, ChecklistItemDto, CommentDto, FileCommentDto, FileDto, FolderDto, PageCommentDto, PageDto, PostDto, TaskDto, TaskListDto, TopicDto } from '@werft/contracts';
import { ApiError } from './api.js';

/**
 * Ausgangskorb für Änderungen (Paket P3 „Speicherqualität“).
 *
 * Jede Änderung wird zuerst hier abgelegt (localStorage) und erst danach gesendet. Dadurch gilt:
 * - Netz weg, Antwort verloren, Tab neu geladen: die Änderung bleibt erhalten und wird mit DERSELBEN opId erneut
 *   gesendet – der Server erkennt die Wiederholung, es entsteht nichts doppelt.
 * - Die Anzeige = Serverstand + offene Änderungen („Overlay“). Das Overlay überlebt das Neuladen.
 * - Reihenfolge: je Tab streng nacheinander, in der Reihenfolge der Eingabe.
 * - Mehrere Tabs teilen den Speicher; jeder Tab sendet seine eigenen Einträge. Einträge eines geschlossenen Tabs
 *   übernimmt ein anderer, sobald dessen Lebenszeichen fehlt. Doppelt gesendet wäre harmlos (opId).
 * - Einträge sind an die Person gebunden; nach Abmelden/Anmelden einer anderen Person wird nichts fremd gesendet.
 * - Der Server hält opIds 7 Tage; ältere Einträge werden nicht mehr gesendet, sondern gemeldet.
 */

export type Overlay =
  | { type: 'create'; tasks: TaskDto[] }
  | { type: 'patch'; taskId: string; fields: Partial<TaskDto> }
  | { type: 'remove'; taskId: string }
  | { type: 'listCreate'; list: TaskListDto }
  | { type: 'listPatch'; listId: string; fields: Partial<TaskListDto> }
  /** Zusammenführen: Liste verschwindet, ihre Aufgaben erscheinen in der Ziel-Liste */
  | { type: 'listMerge'; sourceId: string; targetId: string }
  /** Kommentare (Paket P6) */
  | { type: 'comment'; comment: CommentDto }
  | { type: 'commentPatch'; taskId: string; commentId: string; fields: Partial<CommentDto> }
  | { type: 'commentRemove'; taskId: string; commentId: string }
  /** Checkliste (Paket P8) */
  | { type: 'check'; item: ChecklistItemDto }
  | { type: 'checkPatch'; taskId: string; itemId: string; fields: Partial<ChecklistItemDto> }
  | { type: 'checkRemove'; taskId: string; itemId: string }
  /** Ablage (Paket P11) */
  | { type: 'filePatch'; fileId: string; fields: Partial<FileDto> }
  | { type: 'fileRemove'; fileId: string }
  /** Projektinsel W08: Rückmeldungen an Dateien */
  | { type: 'fileComment'; comment: FileCommentDto }
  | { type: 'fileCommentPatch'; fileId: string; commentId: string; fields: Partial<FileCommentDto> }
  | { type: 'fileCommentRemove'; fileId: string; commentId: string }
  | { type: 'folderCreate'; folder: FolderDto }
  | { type: 'folderPatch'; folderId: string; fields: Partial<FolderDto> }
  /** Notizen (Paket P12) */
  | { type: 'pageCreate'; page: PageDto }
  | { type: 'pagePatch'; pageId: string; fields: Partial<PageDto> }
  | { type: 'pageRemove'; pageIds: string[] }
  | { type: 'pageComment'; comment: PageCommentDto }
  | { type: 'pageCommentPatch'; pageId: string; commentId: string; fields: Partial<PageCommentDto> }
  | { type: 'pageCommentRemove'; pageId: string; commentIds: string[] }
  /** Gespräche (Paket P13) */
  | { type: 'talkTopic'; topic: TopicDto }
  | { type: 'talkTopicPatch'; topicId: string; fields: Partial<TopicDto> }
  | { type: 'talkTopicRemove'; topicId: string }
  | { type: 'talkPost'; post: PostDto }
  | { type: 'talkPostPatch'; topicId: string; postId: string; fields: Partial<PostDto> }
  | { type: 'talkPostRemove'; topicId: string; postId: string }
  | { type: 'talkMessage'; message: ChatMessageDto }
  | { type: 'talkMessagePatch'; messageId: string; fields: Partial<ChatMessageDto> }
  | { type: 'talkMessageRemove'; messageId: string }
  /** Bereiche (Paket P14) */
  | { type: 'areaCreate'; area: AreaDto }
  | { type: 'areaPatch'; areaId: string; fields: Partial<AreaDto> }
  /** Audit F07: Seite, Datei oder Thema an die Übersicht heften/lösen */
  | { type: 'pin'; kind: 'page' | 'file' | 'topic'; targetId: string; on: boolean }
  /** Audit F10: Verknüpfung gesetzt/gelöst (Schlüssel „art:id|art:id“, sortiert) */
  | { type: 'link'; key: string; on: boolean };

export interface OutboxEntry {
  opId: string;
  userId: string;
  tab: string;
  kind: string;
  projectId: string;
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  url: string;
  body: unknown;
  createdAt: number;
  overlay?: Overlay;
  /** Fachliche Zusatzangaben für die Auswertung (z. B. Ausgangsstand für Konfliktprüfung) */
  meta?: Record<string, unknown>;
}

export type NewEntry = Omit<OutboxEntry, 'userId' | 'tab' | 'createdAt'>;

export interface Handler {
  /** Erfolg: Serverantwort übernehmen */
  done(e: OutboxEntry, result: unknown): void;
  /**
   * Fachliche Ablehnung. Rückgabe = geänderter Eintrag, der stattdessen gesendet wird (z. B. neu aufgesetzt auf
   * den aktuellen Stand), sonst wird der Eintrag verworfen.
   */
  fail(e: OutboxEntry, err: unknown): Partial<OutboxEntry> | void;
  /** Zu alt zum Senden */
  expired?(e: OutboxEntry): void;
}

export interface OutboxStatus {
  /** offene Änderungen der angemeldeten Person (alle Tabs) */
  pending: number;
  /** letzter Sendeversuch scheiterte am Netz/Server – es wird automatisch erneut versucht */
  waiting: boolean;
}

export interface OutboxDeps {
  send(e: OutboxEntry): Promise<unknown>;
  /** gemeinsamer Speicher; undefined = nicht verfügbar */
  load(): OutboxEntry[] | undefined;
  save(entries: OutboxEntry[]): boolean;
  loadTabs(): Record<string, number> | undefined;
  saveTabs(tabs: Record<string, number>): void;
  now(): number;
  tab: string;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(t: unknown): void;
}

export const MAX_AGE_MS = 6 * 86_400_000;
const TAB_STALE_MS = 20_000;

/** Ausgang unbekannt oder Server vorübergehend nicht erreichbar → später erneut senden */
export function isTransient(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 0 || err.status === 401 || err.status === 408 || err.status === 429 || err.status >= 500);
}

export class Outbox {
  private mem: OutboxEntry[] = [];
  private handlers = new Map<string, Handler>();
  private waiters = new Map<string, { resolve: (v: unknown) => void; reject: (e: unknown) => void }>();
  private listeners = new Set<() => void>();
  /** ADR 0175: Abschluss je Eintrag (bestätigt/abgelehnt) – für den objektbezogenen Speicherstatus */
  private observers = new Set<(e: OutboxEntry, ok: boolean, err?: unknown) => void>();
  private userId: string | null = null;
  private running = false;
  private inflight: string | null = null;
  private delay = 0;
  private timer: unknown;
  private status: OutboxStatus = { pending: 0, waiting: false };
  private version = 0;
  /** true, wenn Einträge nur im Arbeitsspeicher liegen (localStorage gesperrt) */
  volatile = false;

  constructor(private d: OutboxDeps) {
    this.mem = d.load() ?? [];
    this.volatile = d.load() === undefined;
    this.recompute();
  }

  register(kind: string, h: Handler) {
    this.handlers.set(kind, h);
  }

  /** Angemeldete Person setzen (null = abgemeldet); startet das Senden */
  setUser(id: string | null) {
    this.userId = id;
    this.recompute();
    this.emit();
    if (id) this.kick();
  }

  enqueue<T = unknown>(n: NewEntry): Promise<T> {
    if (!this.userId) return Promise.reject(new Error('Nicht angemeldet.'));
    const entry: OutboxEntry = { ...n, userId: this.userId, tab: this.d.tab, createdAt: this.d.now() };
    const p = new Promise<T>((resolve, reject) => this.waiters.set(entry.opId, { resolve: resolve as (v: unknown) => void, reject }));
    this.mutate((l) => [...l, entry]);
    this.kick();
    return p;
  }

  /** Noch nicht gesendeten Eintrag zurücknehmen (z. B. „Rückgängig“ offline). true = zurückgenommen */
  cancel(opId: string): boolean {
    if (this.inflight === opId || !this.mem.some((e) => e.opId === opId)) return false;
    this.mutate((l) => l.filter((e) => e.opId !== opId));
    this.waiters.get(opId)?.resolve(undefined);
    this.waiters.delete(opId);
    return true;
  }

  /** Offene Einträge der angemeldeten Person, optional je Projekt (für die Anzeige) */
  entries(projectId?: string): OutboxEntry[] {
    return this.mem.filter((e) => e.userId === this.userId && (!projectId || e.projectId === projectId));
  }

  /** Einträge der angemeldeten Person verwerfen (Abmelden trotz offener Änderungen) */
  discardMine() {
    const mine = new Set(this.entries().map((e) => e.opId));
    this.mutate((l) => l.filter((e) => !mine.has(e.opId)));
    for (const id of mine) {
      this.waiters.get(id)?.reject(new Error('Verworfen.'));
      this.waiters.delete(id);
    }
  }

  /** Abschluss eines Eintrags beobachten (Ersatzeinträge nach Konflikt-Neuaufsatz zählen als noch offen) */
  observe(fn: (e: OutboxEntry, ok: boolean, err?: unknown) => void) {
    this.observers.add(fn);
    return () => void this.observers.delete(fn);
  }

  /** Läuft gerade genau dieser Eintrag? */
  isInflight(opId: string) {
    return this.inflight === opId;
  }

  getUser = () => this.userId;
  getStatus = () => this.status;
  getVersion = () => this.version;
  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  /** Speicher wurde von einem anderen Tab geändert */
  reload() {
    const l = this.d.load();
    if (l) this.mem = l;
    this.recompute();
    this.emit();
  }

  /** Lebenszeichen schreiben und Einträge verwaister Tabs übernehmen */
  heartbeat() {
    const now = this.d.now();
    const tabs = { ...(this.d.loadTabs() ?? {}) };
    for (const [id, ts] of Object.entries(tabs)) if (now - ts > 7 * 86_400_000) delete tabs[id];
    tabs[this.d.tab] = now;
    this.d.saveTabs(tabs);
    const orphan = (e: OutboxEntry) => e.tab !== this.d.tab && e.userId === this.userId && now - (tabs[e.tab] ?? 0) > TAB_STALE_MS;
    if (this.userId && this.mem.some(orphan)) {
      this.mutate((l) => l.map((e) => (orphan(e) ? { ...e, tab: this.d.tab } : e)));
      this.kick();
    }
  }

  /** Jetzt senden (Netz wieder da, Tab sichtbar, neuer Eintrag) */
  wake() {
    this.delay = 0;
    this.d.clearTimer(this.timer);
    this.timer = undefined;
    this.kick();
  }

  private kick() {
    if (!this.running && !this.timer) void this.pump();
  }

  private next(): OutboxEntry | undefined {
    return this.mem.find((e) => e.userId === this.userId && e.tab === this.d.tab);
  }

  private async pump() {
    if (this.running || !this.userId) return;
    this.running = true;
    try {
      for (;;) {
        const e = this.next();
        if (!e) break;
        // Ohne registrierte Aktion (Modul noch nicht geladen, z. B. nach Neuladen in einem anderen Werkzeug) trotzdem
        // senden – die Änderung darf nicht verloren gehen; nur die Anzeige-Nacharbeit (done/fail) entfällt.
        const h = this.handlers.get(e.kind);
        if (this.d.now() - e.createdAt > MAX_AGE_MS) {
          this.drop(e.opId);
          h?.expired?.(e);
          this.notify(e, false, new Error('Zu alt.'));
          this.settle(e.opId, false, new Error('Zu alt.'));
          continue;
        }
        this.inflight = e.opId;
        let result: unknown;
        try {
          result = await this.d.send(e);
        } catch (err) {
          this.inflight = null;
          if (isTransient(err)) {
            this.setWaiting(true);
            this.delay = Math.min(30_000, this.delay ? this.delay * 2 : 1000);
            this.timer = this.d.setTimer(() => {
              this.timer = undefined;
              void this.pump();
            }, this.delay * (0.8 + Math.random() * 0.4));
            return;
          }
          this.setWaiting(false);
          let replacement: Partial<OutboxEntry> | void = undefined;
          try {
            replacement = h ? h.fail(e, err) : console.warn('Ausgangskorb: abgelehnt', e.kind, err);
          } catch (inner) {
            console.error('Ausgangskorb', inner);
          }
          if (replacement) {
            const r = { ...e, ...replacement };
            this.mutate((l) => l.map((x) => (x.opId === e.opId ? r : x)));
            const w = this.waiters.get(e.opId);
            if (w && r.opId !== e.opId) {
              this.waiters.delete(e.opId);
              this.waiters.set(r.opId, w);
            }
          } else {
            this.drop(e.opId);
            this.notify(e, false, err);
            this.settle(e.opId, false, err);
          }
          continue;
        }
        this.inflight = null;
        this.delay = 0;
        this.setWaiting(false);
        // Erst die Serverfassung übernehmen, dann das Overlay entfernen – so flackert nichts
        try {
          h?.done(e, result);
        } catch (inner) {
          console.error('Ausgangskorb', inner);
        }
        this.drop(e.opId);
        this.notify(e, true);
        this.settle(e.opId, true, result);
      }
    } finally {
      this.running = false;
      this.inflight = null;
    }
  }

  private notify(e: OutboxEntry, ok: boolean, err?: unknown) {
    for (const fn of this.observers) {
      try {
        fn(e, ok, err);
      } catch (inner) {
        console.error('Ausgangskorb', inner);
      }
    }
  }

  private settle(opId: string, ok: boolean, v: unknown) {
    const w = this.waiters.get(opId);
    if (!w) return;
    this.waiters.delete(opId);
    if (ok) w.resolve(v);
    else w.reject(v);
  }

  private drop(opId: string) {
    this.mutate((l) => l.filter((x) => x.opId !== opId));
  }

  private mutate(fn: (l: OutboxEntry[]) => OutboxEntry[]) {
    // Immer vom gemeinsamen Stand ausgehen, damit Einträge anderer Tabs nicht verloren gehen
    const base = this.d.load() ?? this.mem;
    this.mem = fn(base);
    if (!this.d.save(this.mem)) this.volatile = true;
    this.recompute();
    this.emit();
  }

  private setWaiting(w: boolean) {
    if (this.status.waiting === w) return;
    this.status = { ...this.status, waiting: w };
    this.emit();
  }

  private recompute() {
    const pending = this.mem.filter((e) => e.userId === this.userId).length;
    if (pending !== this.status.pending) this.status = { ...this.status, pending };
    if (pending === 0 && this.status.waiting) this.status = { ...this.status, waiting: false };
    this.version++;
  }

  private emit() {
    this.listeners.forEach((l) => l());
  }
}

/** Anzeige = Serverstand + offene Änderungen */
export function applyOverlays(tasks: TaskDto[], entries: OutboxEntry[]): TaskDto[] {
  if (!entries.length) return tasks;
  let out = tasks;
  for (const e of entries) {
    const o = e.overlay;
    if (!o) continue;
    if (o.type === 'create') {
      const known = new Set(out.map((x) => x.id));
      out = [...out, ...o.tasks.filter((x) => !known.has(x.id))];
    } else if (o.type === 'patch') out = out.map((x) => (x.id === o.taskId ? { ...x, ...o.fields } : x));
    else if (o.type === 'remove') out = out.filter((x) => x.id !== o.taskId);
    else if (o.type === 'listMerge') out = out.map((x) => (x.listId === o.sourceId ? { ...x, listId: o.targetId, position: `\uffff${x.position}` } : x));
  }
  return out;
}

/** Kommentare einer Aufgabe = Serverstand + offene Kommentar-Änderungen */
export function applyCommentOverlays(comments: CommentDto[], entries: OutboxEntry[], taskId: string): CommentDto[] {
  let out = comments;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'comment' && o.comment.taskId === taskId) out = out.some((c) => c.id === o.comment.id) ? out : [...out, o.comment];
    else if (o?.type === 'commentPatch' && o.taskId === taskId) out = out.map((c) => (c.id === o.commentId ? { ...c, ...o.fields } : c));
    else if (o?.type === 'commentRemove' && o.taskId === taskId) out = out.filter((c) => c.id !== o.commentId);
  }
  return out;
}

/** Checkliste einer Aufgabe = Serverstand + offene Änderungen, nach Position */
export function applyChecklistOverlays(items: ChecklistItemDto[], entries: OutboxEntry[], taskId: string): ChecklistItemDto[] {
  let out = items;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'check' && o.item.taskId === taskId) out = out.some((c) => c.id === o.item.id) ? out : [...out, o.item];
    else if (o?.type === 'checkPatch' && o.taskId === taskId) out = out.map((c) => (c.id === o.itemId ? { ...c, ...o.fields } : c));
    else if (o?.type === 'checkRemove' && o.taskId === taskId) out = out.filter((c) => c.id !== o.itemId);
  }
  return out === items ? items : [...out].sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : a.id < b.id ? -1 : 1));
}

/**
 * Ordnerinhalt = Serverstand + offene Ablage-Änderungen. Eine verschobene Datei verschwindet aus ihrem alten Ordner
 * (ein neuer Ordner zeigt sie nach Bestätigung), umbenannte und gelöschte Einträge wirken sofort.
 */
export function applyFileOverlays<T extends { folders: FolderDto[]; files: FileDto[] }>(content: T, entries: OutboxEntry[], folderId: string | null): T {
  let { folders, files } = content;
  let changed = false;
  for (const e of entries) {
    const o = e.overlay;
    if (!o) continue;
    if (o.type === 'filePatch') {
      files = files.map((f) => (f.id === o.fileId ? { ...f, ...o.fields } : f)).filter((f) => f.folderId === folderId);
      changed = true;
    } else if (o.type === 'fileRemove') {
      files = files.filter((f) => f.id !== o.fileId);
      changed = true;
    } else if (o.type === 'folderCreate') {
      if (!folders.some((f) => f.id === o.folder.id)) folders = [...folders, o.folder];
      changed = true;
    } else if (o.type === 'folderPatch') {
      folders = folders.map((f) => (f.id === o.folderId ? { ...f, ...o.fields } : f));
      changed = true;
    }
  }
  if (!changed) return content;
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' });
  return { ...content, folders: [...folders].sort(byName), files: [...files].sort(byName) };
}

const byPosition = <T extends { position: string; id: string }>(a: T, b: T) => (a.position < b.position ? -1 : a.position > b.position ? 1 : a.id < b.id ? -1 : 1);

/** Seiten eines Projekts = Serverstand + offene Änderungen (anlegen, umbenennen, verschieben, löschen) */
export function applyPageOverlays(pages: PageDto[], entries: OutboxEntry[]): PageDto[] {
  let out = pages;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'pageCreate') out = out.some((p) => p.id === o.page.id) ? out : [...out, o.page];
    else if (o?.type === 'pagePatch') out = out.map((p) => (p.id === o.pageId ? { ...p, ...o.fields } : p));
    else if (o?.type === 'pageRemove') out = out.filter((p) => !o.pageIds.includes(p.id));
  }
  return out === pages ? pages : [...out].sort(byPosition);
}

/** Kommentare einer Seite = Serverstand + offene Änderungen */
export function applyPageCommentOverlays(comments: PageCommentDto[], entries: OutboxEntry[], pageId: string): PageCommentDto[] {
  let out = comments;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'pageComment' && o.comment.pageId === pageId) out = out.some((c) => c.id === o.comment.id) ? out : [...out, o.comment];
    else if (o?.type === 'pageCommentPatch' && o.pageId === pageId) out = out.map((c) => (c.id === o.commentId ? { ...c, ...o.fields } : c));
    else if (o?.type === 'pageCommentRemove' && o.pageId === pageId) out = out.filter((c) => !o.commentIds.includes(c.id));
  }
  return out;
}

/** W08: Rückmeldungen einer Datei = Serverstand + offene Änderungen */
export function applyFileCommentOverlays(comments: FileCommentDto[], entries: OutboxEntry[], fileId: string): FileCommentDto[] {
  let out = comments;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'fileComment' && o.comment.fileId === fileId) out = out.some((c) => c.id === o.comment.id) ? out : [...out, o.comment];
    else if (o?.type === 'fileCommentPatch' && o.fileId === fileId) out = out.map((c) => (c.id === o.commentId ? { ...c, ...o.fields } : c));
    else if (o?.type === 'fileCommentRemove' && o.fileId === fileId) {
      const id = o.commentId;
      // wie der Server: eine Rückmeldung mit Antworten bleibt als Platzhalter stehen
      out = out.some((c) => c.parentId === id && !c.deleted) ? out.map((c) => (c.id === id ? { ...c, deleted: true, body: '' } : c)) : out.filter((c) => c.id !== id);
    }
  }
  return out;
}

/** Themen = Serverstand + offene Änderungen; neue zuerst (nach letzter Aktivität) */
export function applyTopicOverlays<T extends TopicDto>(topics: T[], entries: OutboxEntry[], state: 'open' | 'resolved'): T[] {
  let out = topics;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'talkTopic' && state === 'open') out = out.some((x) => x.id === o.topic.id) ? out : [{ ...o.topic, unread: false } as unknown as T, ...out];
    else if (o?.type === 'talkTopicPatch') out = out.map((x) => (x.id === o.topicId ? { ...x, ...o.fields } : x));
    else if (o?.type === 'talkTopicRemove') out = out.filter((x) => x.id !== o.topicId);
  }
  if (out === topics) return topics;
  return out.filter((x) => (state === 'open' ? !x.resolvedAt : !!x.resolvedAt)).sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
}

export function applyTopicDetailOverlays<T extends { topic: TopicDto; posts: PostDto[] }>(d: T, entries: OutboxEntry[]): T {
  let { topic, posts } = d;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'talkTopicPatch' && o.topicId === topic.id) topic = { ...topic, ...o.fields };
    else if (o?.type === 'talkPost' && o.post.topicId === topic.id) posts = posts.some((x) => x.id === o.post.id) ? posts : [...posts, o.post];
    else if (o?.type === 'talkPostPatch' && o.topicId === topic.id) posts = posts.map((x) => (x.id === o.postId ? { ...x, ...o.fields } : x));
    else if (o?.type === 'talkPostRemove' && o.topicId === topic.id) posts = posts.filter((x) => x.id !== o.postId);
  }
  return topic === d.topic && posts === d.posts ? d : { ...d, topic, posts };
}

/** Chat = Serverstand (alt → neu) + offene Nachrichten (ans Ende) */
export function applyMessageOverlays(messages: ChatMessageDto[], entries: OutboxEntry[]): ChatMessageDto[] {
  let out = messages;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'talkMessage') out = out.some((x) => x.id === o.message.id) ? out : [...out, o.message];
    else if (o?.type === 'talkMessagePatch') out = out.map((x) => (x.id === o.messageId ? { ...x, ...o.fields } : x));
    else if (o?.type === 'talkMessageRemove') out = out.filter((x) => x.id !== o.messageId);
  }
  return out;
}

/** Bereiche = Serverstand + offene Änderungen, nach Position */
export function applyAreaOverlays(areas: AreaDto[], entries: OutboxEntry[]): AreaDto[] {
  let out = areas;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'areaCreate') out = out.some((a) => a.id === o.area.id) ? out : [...out, o.area];
    else if (o?.type === 'areaPatch') out = out.map((a) => (a.id === o.areaId ? { ...a, ...o.fields } : a));
  }
  return out === areas ? areas : [...out].sort(byPosition);
}

/** Listen = Serverstand + offene Listen-Änderungen, nach Position sortiert */
export function applyListOverlays(lists: TaskListDto[], entries: OutboxEntry[]): TaskListDto[] {
  let out = lists;
  for (const e of entries) {
    const o = e.overlay;
    if (o?.type === 'listCreate') out = out.some((l) => l.id === o.list.id) ? out : [...out, o.list];
    else if (o?.type === 'listPatch') out = out.map((l) => (l.id === o.listId ? { ...l, ...o.fields } : l));
    else if (o?.type === 'listMerge') out = out.filter((l) => l.id !== o.sourceId);
  }
  return out === lists ? lists : [...out].sort((a, b) => (a.position < b.position ? -1 : a.position > b.position ? 1 : a.id < b.id ? -1 : 1));
}

export function overlayTask(task: TaskDto | undefined, entries: OutboxEntry[]): TaskDto | undefined {
  if (!task) return task;
  return applyOverlays([task], entries)[0];
}

export function useOutboxStatus(box: Outbox): OutboxStatus {
  return useSyncExternalStore(box.subscribe, box.getStatus);
}

/** Offene Einträge eines Projekts; neu berechnet, wenn sich der Ausgangskorb ändert */
export function useOutboxEntries(box: Outbox, projectId?: string): OutboxEntry[] {
  useSyncExternalStore(box.subscribe, box.getVersion);
  return box.entries(projectId);
}
